const kids_holidayRequests = new Map();

async function kids_getHolidaysES () {
	const kids_currentYear = new Date().getFullYear();
	const kids_years = [ kids_currentYear, kids_currentYear + 1 ];
	const kids_cacheKey = kids_years.join( '-' );

	if ( !kids_holidayRequests.has( kids_cacheKey ) ) {
		const kids_request = Promise.all(
			kids_years.map( async kids_year => {
				const kids_response = await fetch( `https://date.nager.at/api/v3/PublicHolidays/${kids_year}/ES` );

				if ( !kids_response.ok ) {
					throw new Error( `Holiday request failed with status ${kids_response.status}` );
				}

				return kids_response.json();
			} )
		).then( kids_responses => kids_responses.flat().map( kids_holiday => kids_holiday.date ) );

		kids_holidayRequests.set( kids_cacheKey, kids_request );
		kids_request.catch( () => kids_holidayRequests.delete( kids_cacheKey ) );
	}

	return kids_holidayRequests.get( kids_cacheKey );
}

function kids_isWeekend ( kids_date ) {
	const kids_day = kids_date.getDay();
	return kids_day === 0 || kids_day === 6;
}

function kids_toLocalISODate ( kids_date ) {
	const kids_year = kids_date.getFullYear();
	const kids_month = String( kids_date.getMonth() + 1 ).padStart( 2, '0' );
	const kids_day = String( kids_date.getDate() ).padStart( 2, '0' );

	return `${kids_year}-${kids_month}-${kids_day}`;
}

function kids_isHoliday ( kids_date, kids_holidays ) {
	return kids_holidays.includes( kids_toLocalISODate( kids_date ) );
}

function kids_addBusinessDays ( kids_startDate, kids_daysToAdd, kids_holidays ) {
	const kids_date = new Date( kids_startDate );
	let kids_addedDays = 0;

	while ( kids_addedDays < kids_daysToAdd ) {
		kids_date.setDate( kids_date.getDate() + 1 );

		if ( !kids_isWeekend( kids_date ) && !kids_isHoliday( kids_date, kids_holidays ) ) {
			kids_addedDays++;
		}
	}

	return kids_date;
}

function kids_formatDateLocalized ( kids_date, kids_locale = 'es-ES' ) {
	return new Intl.DateTimeFormat( kids_locale, {
		day: '2-digit',
		month: 'short'
	} ).format( kids_date );
}

function kids_formatDateRangeLocalized ( kids_dateMin, kids_dateMax, kids_locale = 'es-ES' ) {
	return new Intl.DateTimeFormat( kids_locale, {
		day: '2-digit',
		month: 'short'
	} ).formatRange( kids_dateMin, kids_dateMax ).replace( /[–—-]/g, ' - ' );
}

async function kids_getShippingTime ( {
	kids_minDays = 2,
	kids_maxDays = 5,
	kids_cutoffHour = 10.5,
	kids_locale = 'es-ES'
} = {} ) {
	const kids_now = new Date();
	const kids_holidays = await kids_getHolidaysES();
	const kids_startDate = new Date( kids_now );
	const kids_nowHourDecimal = kids_now.getHours() + kids_now.getMinutes() / 60;

	if ( kids_nowHourDecimal >= kids_cutoffHour ) {
		kids_startDate.setDate( kids_startDate.getDate() + 1 );
	}

	while ( kids_isWeekend( kids_startDate ) || kids_isHoliday( kids_startDate, kids_holidays ) ) {
		kids_startDate.setDate( kids_startDate.getDate() + 1 );
	}

	const kids_dateMin = kids_addBusinessDays( kids_startDate, kids_minDays, kids_holidays );
	const kids_daysExtra = kids_maxDays - kids_minDays;
	const kids_dateMax = kids_addBusinessDays( kids_dateMin, kids_daysExtra, kids_holidays );

	return {
		kids_min: kids_formatDateLocalized( kids_dateMin, kids_locale ),
		kids_max: kids_formatDateLocalized( kids_dateMax, kids_locale ),
		kids_range: kids_formatDateRangeLocalized( kids_dateMin, kids_dateMax, kids_locale ),
		kids_sameMonth: kids_dateMin.getMonth() === kids_dateMax.getMonth() && kids_dateMin.getFullYear() === kids_dateMax.getFullYear()
	};
}

if ( !customElements.get( 'kids-product-shipping-info' ) ) {
	customElements.define(
		'kids-product-shipping-info',
		class kids_ProductShippingInfo extends HTMLElement {
			constructor () {
				super();
				this.kids_renderVersion = 0;
			}

			connectedCallback () {
				this.kids_renderVersion++;
				this.setAttribute( 'aria-busy', 'true' );
				this.kids_render( this.kids_renderVersion );
			}

			disconnectedCallback () {
				this.kids_renderVersion++;
			}

			async kids_render ( kids_renderVersion ) {
				const kids_message = this.dataset.message;

				if ( kids_message ) {
					this.innerHTML = `<span data-type="warning">${kids_message}</span>`;
					this.removeAttribute( 'aria-busy' );
					return;
				}

				const kids_minDays = Number.parseInt( this.dataset.min, 10 );
				const kids_maxDays = Number.parseInt( this.dataset.max, 10 );
				const kids_locale = this.dataset.locale || 'es-ES';
				const kids_template = this.dataset.template;
				const kids_templateSameMonth = this.dataset.templateSameMonth;
				const kids_tone = this.dataset.tone === 'warning' ? 'warning' : 'normal';

				try {
					if ( !Number.isFinite( kids_minDays ) || !Number.isFinite( kids_maxDays ) ) {
						throw new Error( 'Shipping estimate days are not available' );
					}

					const {
						kids_min,
						kids_max,
						kids_range,
						kids_sameMonth
					} = await kids_getShippingTime( {
						kids_minDays,
						kids_maxDays,
						kids_locale
					} );

					if ( !this.isConnected || kids_renderVersion !== this.kids_renderVersion ) {
						return;
					}

					const kids_dateMinHtml = `<strong class="kids_product-info-shipping-date">${kids_min}</strong>`;
					const kids_dateMaxHtml = `<strong class="kids_product-info-shipping-date">${kids_max}</strong>`;
					const kids_dateRangeHtml = `<strong class="kids_product-info-shipping-date">${kids_range}</strong>`;
					const kids_hasSameMonthTemplate = kids_templateSameMonth && !kids_templateSameMonth.includes( 'Translation missing' );
					const kids_result = kids_sameMonth && kids_hasSameMonthTemplate
						? kids_templateSameMonth.replace( '%s', kids_dateRangeHtml )
						: kids_template.replace( '%s', kids_dateMinHtml ).replace( '%s', kids_dateMaxHtml );
					const kids_content = this.dataset.messageAlt
						? `${this.dataset.messageAlt} ${kids_result}`
						: kids_result;

					this.innerHTML = `<span data-type="${kids_tone}">${kids_content}</span>`;
				}
				catch ( kids_error ) {
					if ( !this.isConnected || kids_renderVersion !== this.kids_renderVersion ) {
						return;
					}

					console.error( 'Error calculando el plazo de entrega:', kids_error );
					this.textContent = this.dataset.unavailable;
				}
				finally {
					if ( this.isConnected && kids_renderVersion === this.kids_renderVersion ) {
						this.removeAttribute( 'aria-busy' );
					}
				}
			}
		}
	);
}
